import { Divider, Select, Spin } from 'antd'
import { SectionContainer } from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { FfsLevel } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { FfsSharedValuesContext } from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import { useFfsStrategy } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/FfsStrategyContext'
import {
  FfsMawpSection,
  FfsMawpSectionProps,
} from 'features/indicationTriage/cards/ffsCalc/mawpSection/FfsMawpSection'
import React, { FC, useContext, useMemo } from 'react'
import styled from 'styled-components'

import {
  FfsThicknessCriteriaSection,
  FfsThicknessCriteriaSectionProps,
} from './FfsThicknessCriteriaSection'

// Reusable component for criteria section dividers
interface CriteriaSectionDividerProps {
  title: string
}

const DividerSpan = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #666;
`

const ThicknessSelectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`

const CriteriaSectionDivider: FC<CriteriaSectionDividerProps> = ({ title }) => (
  <Divider titlePlacement='start' orientationMargin='0'>
    <DividerSpan>{title}</DividerSpan>
  </Divider>
)

type PartFourFinalAcceptabilityCriteria = 'mawp' | 'thickness'

interface FinalAcceptabilityCriteriaProps {
  failingThicknessCriteria: boolean
  failingMawpCriteria: boolean
}
export interface FinalAcceptabilityCriteriaCheckResult {
  failingCriteria: ReadonlySet<PartFourFinalAcceptabilityCriteria>
  passesFac: boolean // True if both MAWP and thickness criteria pass
}

export function useFinalAcceptabilityCriteriaCheck({
  failingThicknessCriteria,
  failingMawpCriteria,
}: FinalAcceptabilityCriteriaProps): FinalAcceptabilityCriteriaCheckResult {
  return useMemo(() => {
    const failingCriteria = new Set<PartFourFinalAcceptabilityCriteria>()
    if (failingThicknessCriteria) failingCriteria.add('thickness')
    if (failingMawpCriteria) failingCriteria.add('mawp')

    const passesFac = !failingThicknessCriteria && !failingMawpCriteria

    return {
      failingCriteria,
      passesFac,
    }
  }, [failingThicknessCriteria, failingMawpCriteria])
}

export interface FfsFinalAcceptabilityCriteriaSectionProps {
  loading: boolean
  mawpProps: Omit<
    FfsMawpSectionProps,
    'loading' | 'ffsLevel' | 'addLongitudinalStress' | 'showMawpCriteria'
  >
  thicknessCriteriaCheckProps: Omit<FfsThicknessCriteriaSectionProps, 'loading'>
  ffsLevel: FfsLevel
}

const RawFfsFinalAcceptabilityCriteriaSection: FC<
  FfsFinalAcceptabilityCriteriaSectionProps
> = ({ loading, mawpProps, thicknessCriteriaCheckProps, ffsLevel }) => {
  const {
    selectedMawpCalcThickness,
    setSelectedMawpCalcThickness,
    calcType,
    setCalcType,
  } = useContext(FfsSharedValuesContext)
  const { strategy } = useFfsStrategy()

  return (
    <Spin spinning={loading}>
      <SectionContainer>
        <ThicknessSelectRow>
          <span>Thickness for MAWP:</span>
          <Select
            value={selectedMawpCalcThickness}
            onChange={setSelectedMawpCalcThickness}
            style={{ width: 300 }}
            options={strategy.mawp.mawpCalcThicknessOptions.map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))}
          />
        </ThicknessSelectRow>

        {strategy.mawp.mawpCalcTypeOptions.length > 0 && (
          <ThicknessSelectRow>
            <span>Calculation Type:</span>
            <Select
              value={calcType}
              onChange={setCalcType}
              style={{ width: 300 }}
              options={strategy.mawp.mawpCalcTypeOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
            />
          </ThicknessSelectRow>
        )}

        <CriteriaSectionDivider title='MAWP Criteria' />
        <FfsMawpSection
          {...mawpProps}
          loading={loading}
          ffsLevel={ffsLevel}
          addLongitudinalStress={true}
          showMawpCriteria={true}
        />

        <CriteriaSectionDivider title='Minimum Measured Thickness Criteria' />
        <FfsThicknessCriteriaSection
          {...thicknessCriteriaCheckProps}
          loading={loading}
        />
      </SectionContainer>
    </Spin>
  )
}

export const FfsFinalAcceptabilityCriteriaSection = React.memo(
  RawFfsFinalAcceptabilityCriteriaSection
)
